import React, { useMemo, useState } from 'react';
import { createContext } from '@shared/utils/createContext';

type WebsiteV2OnboardingContext = Readonly<{
  /** The user opted into the WebsiteV2 beta on the create form's choice screen. */
  isBetaChosen: boolean;
  setBetaChosen: (chosen: boolean) => void;
}>;

const [Provider, useWebsiteV2OnboardingRaw] = createContext<WebsiteV2OnboardingContext>({ name: 'WebsiteV2Onboarding', strict: false });

/**
 * Non-strict: returns undefined when no provider is mounted — the CreateWeddingForm is also
 * reused outside the funnel (PublicShop create form), where the choice flow must not engage.
 */
const useWebsiteV2Onboarding = (): WebsiteV2OnboardingContext | undefined => useWebsiteV2OnboardingRaw(false);

/**
 * Carries the beta-vs-classic website choice from the create form to the tying-the-knot
 * route, which mounts the WebsiteV2 create experience instead of auto-redirecting when the
 * beta was chosen. Lives in the FormFlowProviders group so it survives that navigation;
 * deliberately NOT persisted — a mid-flow refresh falls back to the classic redirect (the
 * event is already flagged, and the dashboard/editor offer site creation from there).
 */
const WebsiteV2OnboardingProvider: React.FC = ({ children }) => {
  const [isBetaChosen, setBetaChosen] = useState(false);
  const value = useMemo(() => ({ isBetaChosen, setBetaChosen }), [isBetaChosen]);
  return <Provider value={value}>{children}</Provider>;
};

export { WebsiteV2OnboardingProvider, useWebsiteV2Onboarding };
